import { useCallback, useRef, useState } from "react";
import { KdsAggsUpdate } from "../../../config/Services";
import { assertKitchenResponse } from "./kitchenActions";
import { useSnackbar } from "../product/hooks/SnackbarProvider";

const keyOf = (entry) => JSON.stringify([entry?.orderId, entry?.taskId]);

/** Approve Pending portions or complete Processing portions, never whole orders. */
export default function useKdsItemAccept({
  enabled, allowAccept = true, groups, locationId, updatedBy, updatedByName, onUpdated, onAcknowledged,
}) {
  const showSnackbar = useSnackbar();
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const submitted = useRef({ groups: null, keys: new Set() });
  const latest = useRef(null);
  latest.current = { enabled, allowAccept, groups, locationId, updatedBy, onUpdated, onAcknowledged };
  const run = useCallback(async (entries, status, quantity) => {
    const completing = status === "COMPLETED";
    const sourceStatus = completing ? "IN_PROGRESS" : "PENDING";
    const sourceLabel = completing ? "Processing" : "Pending";
    const verb = completing ? "marked ready" : "accepted";
    if (locked.current) throw new Error("An item update is already in progress.");
    const scope = latest.current;
    const isCurrent = () => latest.current.enabled && latest.current.locationId === scope.locationId &&
      latest.current.updatedBy === scope.updatedBy;
    locked.current = true;
    setBusy(true);
    let attempted = false;
    let failure = null;
    try {
      if (!scope.enabled || !locationId || (!completing && !scope.allowAccept)) throw new Error("Item update is not available.");
      if (!entries?.length) throw new Error(`Select ${sourceLabel.toLowerCase()} portions first.`);
      if (submitted.current.groups !== scope.groups) submitted.current = { groups: scope.groups, keys: new Set() };
      const currentEntries = new Map((scope.groups || []).flatMap((group) => group.entries || [])
        .filter((entry) => entry.status === sourceStatus).map((entry) => [keyOf(entry), entry]));
      const variants = new Map(), seen = new Set();
      entries.forEach((entry) => {
        const key = keyOf(entry), current = currentEntries.get(key);
        if (seen.has(key)) return;
        if (submitted.current.keys.has(key)) throw new Error("These portions were already submitted. Refresh the board before trying again.");
        if (!current || !current.variantId || current.variantId !== entry.variantId ||
          Number(current.quantity) !== Number(entry.quantity) || !Number.isFinite(Number(current.quantity)) ||
          Number(current.quantity) <= 0) throw new Error("These portions have changed. Refresh and select them again.");
        seen.add(key);
        const variant = variants.get(current.variantId) || { variantId: current.variantId, totalQuantity: 0 };
        variant.totalQuantity += Number(current.quantity);
        variants.set(current.variantId, variant);
      });
      const totalQuantity = [...variants.values()].reduce((sum, variant) => sum + variant.totalQuantity, 0);
      if (quantity !== undefined) {
        if (!completing || !Number.isInteger(Number(quantity)) || Number(quantity) <= 0 || Number(quantity) > totalQuantity) {
          throw new Error(`Enter a whole quantity between 1 and the ${sourceLabel.toLowerCase()} quantity.`);
        }
        if (variants.size > 1 && Number(quantity) !== totalQuantity) {
          throw new Error("Choose a variant before marking a partial quantity ready.");
        }
      }
      // Named dishes are consolidated across variants; the API updates one variant per request.
      for (const variant of variants.values()) {
        const requestedQuantity = quantity !== undefined && variants.size === 1 ? Number(quantity) : variant.totalQuantity;
        if (!isCurrent()) throw new Error("Kitchen location or view changed. Refresh the board.");
        attempted = true;
        entries.filter((entry) => entry.variantId === variant.variantId)
          .forEach((entry) => submitted.current.keys.add(keyOf(entry)));
        const response = await KdsAggsUpdate({
          variantId: variant.variantId, quantity: requestedQuantity, status,
          locationId, updatedBy, updatedByName,
        });
        assertKitchenResponse(response);
        const envelopes = [response?.data, response?.data?.data, response?.data?.data?.data, response];
        const result = envelopes
          .find((value) => value?.targetStatus != null);
        if (Number(response?.status) >= 400 || envelopes.some((value) => Number(value?.statusCode) >= 400) ||
          !result || Number(result.es) > 0 || result.success === false ||
          ["ERROR", "FAILED", "FAILURE"].includes(result.status) ||
          result.variantId !== variant.variantId || result.targetStatus !== status ||
          Number(result.requestedQuantity) !== requestedQuantity || result.updatedQuantity == null ||
          !Number.isFinite(Number(result.updatedQuantity)) || Number(result.updatedQuantity) < 0 ||
          Number(result.updatedQuantity) > requestedQuantity) {
          throw new Error("The item update could not be confirmed. Refresh the board before trying again.");
        }
        if (isCurrent() && Number(result.updatedQuantity) > 0 && Array.isArray(result.affectedJobIds) && result.affectedJobIds.length) {
          latest.current.onAcknowledged?.(result.affectedJobIds);
        }
        if (Number(result.updatedQuantity) !== requestedQuantity) {
          throw new Error(`Only ${result.updatedQuantity} of ${requestedQuantity} portions were ${verb}. The board will refresh; review the remaining portions.`);
        }
      }
    } catch (error) {
      failure = error;
    } finally {
      // Also refresh after an uncertain/partial write, so retries use current quantities.
      if (attempted && isCurrent()) {
        try { await latest.current.onUpdated?.(); }
        catch (_) { failure = failure || new Error(`Items were ${verb}, but the board could not refresh. Refresh before trying again.`); }
      }
      locked.current = false;
      setBusy(false);
    }
    if (failure) {
      if (isCurrent()) showSnackbar(failure?.response?.data?.message || failure.message || "Unable to update items", "error");
      throw failure;
    }
    if (isCurrent()) showSnackbar(completing ? "Selected portions marked ready" : "Selected portions moved to processing", "success");
  }, [locationId, updatedBy, updatedByName, showSnackbar]);
  const accept = useCallback((entries) => run(entries, "IN_PROGRESS"), [run]);
  const ready = useCallback((entries) => run(entries, "COMPLETED"), [run]);
  const readyQuantity = useCallback((entries, quantity) => run(entries, "COMPLETED", quantity === undefined ? NaN : quantity), [run]);
  return { busy, accept, ready, readyQuantity };
}
